import { EVENT_SOURCE, isKnownEventName } from './eventNames';

/**
 * Builds the `event_logs` document body for one user action.
 *
 * Kept pure and free of Firebase so the parts that are easy to get wrong — resolving who acted and
 * on which conveyor, and keeping params consistent — can be tested directly. `writeEventLog` adds
 * the server timestamp and performs the write.
 *
 * Identity is never passed by the caller: the logger reads it from the session, and every field
 * falls back to `null` so an event logged before sign-in completes is still recorded rather than
 * throwing inside a click handler.
 */

/** Ids reach the app as both strings and numbers; the log always stores strings. */
const ID_KEY_PATTERN = /(^|_)id$/;

/**
 * Normalize one params map: drop what the caller left undefined, keep a deliberate `null`, and
 * stringify ids so a single id never lands in the collection under two different types.
 *
 * @param {Object} eventParams - Raw params from the call site.
 * @returns {Object} Params ready to store.
 */
function normalizeEventParams(eventParams) {
  if (!eventParams || typeof eventParams !== 'object' || Array.isArray(eventParams)) return {};

  return Object.entries(eventParams).reduce((params, [key, value]) => {
    if (value === undefined) return params;

    params[key] = typeof value === 'number' && ID_KEY_PATTERN.test(key) ? String(value) : value;
    return params;
  }, {});
}

/**
 * Build the document body for an event, or `null` when the name is not registered.
 *
 * @param {Object} params
 * @param {string} params.eventName - Name from `EVENT_NAMES`.
 * @param {Object} [params.eventParams={}] - Event params; snake_case keys, scalars or short arrays.
 * @param {Object} [params.session={}] - Current session identity.
 * @param {string} [params.session.uid] - Firebase auth uid (the device account).
 * @param {string} [params.session.client] - Client/company id.
 * @param {string} [params.session.device] - Active conveyor/device id.
 * @param {string} [params.session.operator] - Selected shift operator key (never their name).
 * @returns {Object|null} Document body without `timestamp`, or `null` for an unknown event name.
 */
export function buildEventLog({ eventName, eventParams, session } = {}) {
  if (!isKnownEventName(eventName)) return null;

  return {
    event_name: eventName,
    event_params: normalizeEventParams(eventParams),
    user: session?.uid ?? null,
    client: session?.client ?? null,
    device: session?.device ?? null,
    operator: session?.operator ?? null,
    source: EVENT_SOURCE,
  };
}
